import Select, { CSSObjectWithLabel, GroupBase, Props, StylesConfig, Theme } from "react-select";
import { CreatableAdditionalProps } from "react-select/dist/declarations/src/useCreatable";
import { AsyncPaginate, AsyncPaginateProps } from "react-select-async-paginate";
import AsyncCreatableSelect from "react-select/async-creatable";
import AsyncSelect, { AsyncProps } from "react-select/async";
import CreatableSelect from "react-select/creatable";

import { useThemeMode } from "@/../_metronic/partials";
import { SkeletonWrapper } from "@/components/SkeletonWrapper";

export type Additional = { page: number };

export type AsyncSelectProps = AsyncProps<unknown, boolean, GroupBase<unknown>>;
export type AsyncPaginateSelectProps = AsyncPaginateProps<
	unknown,
	GroupBase<unknown>,
	Additional,
	boolean
>;

export type ReactSelectProps = (
	| Props
	| AsyncProps<unknown, boolean, GroupBase<unknown>>
	| CreatableAdditionalProps<unknown, GroupBase<unknown>>
	| AsyncPaginateSelectProps
) & {
	styles?: StylesConfig<any>;
	isAsync?: boolean;
	isCreatable?: boolean;
	isAsyncPaginate?: boolean;
	isSolid?: boolean;
	skeleton?: boolean;
	skeletonWidth?: string | number;
	skeletonHeight?: string | number;
};

type ReactSelectStyles = {
	styles: StylesConfig<unknown, boolean, GroupBase<unknown>> | undefined;
};

export function ReactSelect({
	styles,
	isAsync,
	isCreatable,
	isAsyncPaginate,
	isSolid,
	skeleton = false,
	skeletonWidth,
	skeletonHeight,
	...props
}: ReactSelectProps) {
	const { mode } = useThemeMode();

	function verifyColorOption(isSelected: boolean, isFocused: boolean) {
		if (mode === "dark") {
			return isSelected ? "#80859b" : isFocused ? "#3e435a" : undefined;
		} else {
			return isSelected ? "#b6c3d3" : isFocused ? "#e0ecfc" : undefined;
		}
	}

	const solidStyle: ReactSelectStyles = isSolid
		? {
				styles: {
					input: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						color: mode === "dark" ? "#92929f" : "#5e6278",
					}),
					placeholder: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						color: mode === "dark" ? "#92929f" : "#5e6278",
					}),
					option: (baseStyles: CSSObjectWithLabel, { isSelected, isFocused }) => ({
						...baseStyles,
						backgroundColor: verifyColorOption(isSelected, isFocused),
					}),
					singleValue: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						color: mode === "dark" ? "#92929f" : "#5e6278",
					}),
					menu: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						backgroundColor: mode === "dark" ? "#1b1b29" : "#f9f9f9",
					}),
					control: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						borderColor: "transparent",
						backgroundColor: mode === "dark" ? "#1b1b29" : "#f9f9f9",
					}),
					multiValue: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						backgroundColor: mode === "dark" ? "#3e435a" : "#E6E6E6",
					}),
					multiValueLabel: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						color: mode === "dark" ? "#e0ecfc" : undefined,
					}),
					multiValueRemove: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						"&:hover": {
							color: mode === "dark" ? "#181C32" : undefined,
							backgroundColor: mode === "dark" ? "#7f7f8a" : undefined,
						},
					}),

					...styles,
				},
		  }
		: {
				styles: {
					option: (baseStyles: CSSObjectWithLabel) => ({
						...baseStyles,
						color: "#1b1b29",
					}),
					...styles,
				},
		  };

	const defaultProps = {
		placeholder: null,
		closeMenuOnSelect: true,
		noOptionsMessage: () => "Nenhum resultado encontrado",
		components: {
			IndicatorSeparator: null,
		},
		theme: (theme: Theme) => ({
			...theme,
			colors: {
				...theme.colors,
				primary: isSolid ? "transparent" : "#b9b9b969",
			},
		}),
	};

	if (isAsync && isCreatable) {
		return (
			<>
				{skeleton ? (
					<SkeletonWrapper width={skeletonWidth} height={skeletonHeight} />
				) : (
					<AsyncCreatableSelect
						loadingMessage={() => "Carregando..."}
						{...defaultProps}
						{...solidStyle}
						{...(props as AsyncSelectProps)}
					/>
				)}
			</>
		);
	} else if (isAsync) {
		return (
			<>
				{skeleton ? (
					<SkeletonWrapper width={skeletonWidth} height={skeletonHeight} />
				) : (
					<AsyncSelect
						loadingMessage={() => "Carregando..."}
						{...defaultProps}
						{...solidStyle}
						{...(props as AsyncSelectProps)}
					/>
				)}
			</>
		);
	} else if (isCreatable) {
		return (
			<>
				{skeleton ? (
					<SkeletonWrapper width={skeletonWidth} height={skeletonHeight} />
				) : (
					<CreatableSelect {...defaultProps} {...solidStyle} {...props} />
				)}
			</>
		);
	} else if (isAsyncPaginate) {
		return (
			<AsyncPaginate
				loadingMessage={() => "Carregando..."}
				{...defaultProps}
				{...solidStyle}
				{...(props as AsyncPaginateSelectProps)}
			/>
		);
	} else {
		return (
			<>
				{skeleton ? (
					<SkeletonWrapper width={skeletonWidth} height={skeletonHeight} />
				) : (
					<Select {...defaultProps} {...solidStyle} {...props} />
				)}
			</>
		);
	}
}
